// FLAGS: none
// One copyable link per language, for the two tabs that hand a URL to a company.
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@openup/shared/primitives/ui/button";
import { Input } from "@openup/shared/primitives/ui/input";

import FormField from "@src/components/spaces/FormField";
import {
  CONTENT_LANGUAGE_LABELS,
  ContentLanguage,
  LocalizedLink,
} from "@src/models/spaces";

interface LocalizedLinkListProps {
  links: LocalizedLink[];
  /** Shown as the field label when a link carries no language. */
  singleLabel: string;
}

/**
 * The language is part of each URL's domain rather than something the recipient
 * picks, so every language gets its own row with its own copy button. Labelled
 * in the language's own name, matching how the public page names them, so a
 * scheduler and a visitor are reading the same words.
 */
const LocalizedLinkList = ({ links, singleLabel }: LocalizedLinkListProps) => {
  const onCopy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied.");
    } catch {
      toast.error("Couldn't copy — copy it manually.");
    }
  };

  const labelFor = (languageCode: string) =>
    languageCode === ""
      ? singleLabel
      : (CONTENT_LANGUAGE_LABELS[languageCode as ContentLanguage] ??
        languageCode);

  return (
    <div className="flex flex-col gap-3">
      {links.map((link) => (
        <FormField
          key={`${link.languageCode}-${link.url}`}
          label={labelFor(link.languageCode)}
        >
          {({ id }) => (
            <div className="flex gap-2">
              <Input id={id} readOnly value={link.url} />
              <Button
                variant="secondary"
                size="default"
                aria-label={`Copy ${labelFor(link.languageCode)} link`}
                onClick={() => onCopy(link.url)}
              >
                <Copy className="mr-1 h-4 w-4" aria-hidden />
                Copy
              </Button>
            </div>
          )}
        </FormField>
      ))}
    </div>
  );
};

export default LocalizedLinkList;
